import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import SearchIcon from "@mui/icons-material/Search";
import {
    CardHeader,
    Grid,
    IconButton,
    InputAdornment, Link,
    Menu,
    MenuItem,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import { withStyles } from "@mui/styles";
import axios from "axios";
import moment from "moment";
import * as React from "react";
import { NavLink } from "react-router-dom";
import { LoadingWrapper } from "../../components/LoadingWrapper";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";

interface SimulationState {
    anchorEl: any;
    anomalies: any[];
    loading: boolean;
    form: PlayerSearchForm;
    totalRecords: number;
    battles: number;
    rowsPerPage: number;
    page: number;
    menuVisible: boolean;
}

export interface PlayerSearchForm {
    keyword: string | undefined;
}

export interface SearchFormProps {
    onFormSubmit(): void;
}

interface PlayersProps {
    activeUser: any;
    activeEnv: any;
    classes: any;
}

class SimulationWrapped extends React.Component<PlayersProps, SimulationState> {

    constructor(props: PlayersProps) {
        super(props);
        // List with grid items
        this.state = {
            anomalies: [],
            loading: true,
            totalRecords: 0,
            battles: 0,
            form: {
                keyword: undefined,
            },
            // Pagination
            page: 0,
            rowsPerPage: 10,
            anchorEl: null,
            menuVisible: false
        };

        document.title = Helpers.documentTitle("Simulation Anomalies");
    }

    componentDidMount() {
        this.fetchAnomalies();
    }

    fetchAnomalies = () => {
        axios.get(`${Config.getInstance.apiUrl}/api/simulation/anomalies`, {
            params: {
                ...this.state.form,
                page: this.state.page,
                rowsPerPage: this.state.rowsPerPage,
            },
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            }
        }).then(response => {
            const data = response.data;

            this.setState({
                loading: false,
                anomalies: data.data || [],
                totalRecords: data.total,
                battles: data.battles
            });
        });
    };

    searchInput = (e: any) => {
        const tempForm = {...this.state.form};
        tempForm.keyword = e.target.value;

        this.setState({form: tempForm});
    };

    searchForm = () => this.setState({loading: true}, () => {
        this.fetchAnomalies();
    });

    filterForm = () => this.setState({loading: false}, () => {
        this.searchForm();
    });

    openRowMenu = (event: any) => this.setState({anchorEl: event.currentTarget}, () => this.setState({menuVisible: true}));
    /**
     * For menu closing we have two functions to make animations smoother
     */
    closeRowMenu = () => this.setState({menuVisible: false});
    clearMenuAnchor = () => this.setState({anchorEl: null});

    handleChangePage = (_event: any, page: number) => this.setState({loading: true, page}, () => {
        this.fetchAnomalies();
    });

    handleChangeRowsPerPage = (event: any) => this.setState({
        loading: true,
        page: 0,
        rowsPerPage: event.target.value,
    }, () => {
        this.fetchAnomalies();
    });

    formatPaginationRange = ({from, to}) => `${from}-${to} of ${this.state.totalRecords}`;

    getFileActions = () => {
        if (!this.state.anchorEl) {
            return "";
        }

        const buttons: any[] = [];
        const anomalyId = this.state.anchorEl.getAttribute("anomalyId");
        const anomaly = this.state.anomalies.find(pl => pl.id === anomalyId);

        if (!anomaly) {
            return "";
        }

        buttons.push(
            <MenuItem key="download-anomaly-key">
                <Link onClick={() => this.download(anomalyId)}
                      style={{width: "100%", textDecoration: "none", color: "#000000"}}>Download</Link>
            </MenuItem>,
        );

        return buttons;
    };

    async download(anomalyId: string) {
        const response = await axios.get(`${Config.getInstance.apiUrl}/api/simulation/anomalies/${anomalyId}/download`, {
            params: {
                ...this.state.form,
                page: this.state.page,
                rowsPerPage: this.state.rowsPerPage,
            },
            headers: {
                Authorization: Helpers.getSessionId(),
                env: this.props.activeEnv,
            }
        });

        this.downloadJson(JSON.stringify(response.data.simulation), "simulation.json", "application/json");
        this.downloadJson(JSON.stringify(response.data.client), "client.json", "application/json");
    }

    getComment(anomaly: { client_hash: string; simulation_hash: string }) {
        if (!anomaly.client_hash) {
            return <ul>
                <li>
                    Force quit / Internet issues / Game crash
                </li>
            </ul>;
        }

        if (!anomaly.simulation_hash) {
            return <ul>
                <li>
                    Internal server issue
                </li>
            </ul>;
        }

        const clientHash = anomaly.client_hash.trim().split(" ");
        const simulationHash = anomaly.simulation_hash.trim().split(" ");

        if (+simulationHash[0] !== 0) {
            return <ul>
                <li>
                    Had errors in simulation (usually means user wanted to deploy units he did not have)
                </li>
            </ul>;
        }

        if (simulationHash[7] !== clientHash[7]) {
            return <ul>
                <li>
                    Total base stats were different (If theses are wrong, probably data in sheets does not match or
                    there
                    were uneven amount of units spawned)
                </li>
            </ul>;
        }

        return <ul>
            {simulationHash[1] !== clientHash[1] && <li>SubStats were different</li>}
            {simulationHash[2] !== clientHash[2] && <li>VIP Bonuses were different</li>}
            {simulationHash[3] !== clientHash[3] && <li>Total hero stats were different</li>}
            {simulationHash[4] !== clientHash[4] && <li>Total ally unit stats were different</li>}
            {simulationHash[5] !== clientHash[5] && <li>Total enemy stats were different</li>}
            {simulationHash[6] !== clientHash[6] && <li>Total building stats were different</li>}
            {simulationHash[8] !== clientHash[8] && <li>Random number hashes were different</li>}
            {simulationHash[9] !== clientHash[9] && <li>Battle result was different</li>}
            {simulationHash[10] !== clientHash[10] && <li>Last step of the battle was different</li>}
            {simulationHash[11] !== clientHash[11] &&
            <li>Hash of all ally unit HP and positions after battle was different</li>}
            {simulationHash[12] !== clientHash[12] &&
            <li>Hash of all building HP and positions after battle was different</li>}
            {simulationHash[13] !== clientHash[13] &&
            <li>Hash of all enemy unit HP and positions after battle was different</li>}
            {simulationHash[14] !== clientHash[14] &&
            <li>Hash of all healer HP and positions after battle was different</li>}
        </ul>;
    }

    downloadJson(content, fileName, contentType) {
        const a = document.createElement("a");
        const file = new Blob([content], {type: contentType});
        a.href = URL.createObjectURL(file);
        a.download = fileName;
        a.click();
    }

    formatTableBody = (items: any[]) => {
        if (this.state.loading) {
            return <TableRow key="loading-row">
                <TableCell colSpan={8}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>
        }

        if (items.length === 0) {
            return <TableRow key="loading-row">
                <TableCell colSpan={8}>
                    <div className="no-items-placeholder">
                        <NotInterestedIcon/>
                        <h3>No anomalies found</h3>
                    </div>
                </TableCell>
            </TableRow>
        }

        return items.map(item => (
            <TableRow
                key={item.id}
                hover={true}
            >
                <TableCell>
                    {item.battle_id}
                </TableCell>
                <TableCell>
                    <NavLink to={`/players/${item.attacker_id}`}>
                        {item.attacker?.name || "Unknown"}
                    </NavLink>
                </TableCell>
                <TableCell>
                    <NavLink to={`/players/${item.defender_id}`}>
                        {item.defender?.name || "Unknown"}
                    </NavLink>
                </TableCell>
                <TableCell>{item.client_hash || "-"}</TableCell>
                <TableCell>{item.simulation_hash || "-"}</TableCell>
                <TableCell>{this.getComment(item)}</TableCell>
                <TableCell>{moment(item.created_at).fromNow()}</TableCell>
                <TableCell>
                    <IconButton
                        aria-label="More"
                        aria-haspopup="true"
                        onClick={this.openRowMenu}
                        anomalyId={item.id}
                    >
                        <MoreVertIcon/>
                    </IconButton>
                </TableCell>
            </TableRow>
        ));
    };

    pageContent(items) {
        return <React.Fragment>
            <Table className="min-table-size">
                <TableHead>
                    <TableRow>
                        <TableCell style={{width: 350}}>Battle ID</TableCell>
                        <TableCell style={{width: 120}}>Attacker</TableCell>
                        <TableCell style={{width: 120}}>Defender</TableCell>
                        <TableCell>Client Hash</TableCell>
                        <TableCell>Simulation Hash</TableCell>
                        <TableCell>Comment</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell style={{width: 40}}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {this.formatTableBody(items)}
                </TableBody>
                {!this.state.loading && items.length > 0 && <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            count={this.state.totalRecords}
                            rowsPerPage={this.state.rowsPerPage}
                            page={this.state.page}
                            onPageChange={this.handleChangePage}
                            onRowsPerPageChange={this.handleChangeRowsPerPage}
                            labelDisplayedRows={this.formatPaginationRange}
                        />
                    </TableRow>
                </TableFooter>}
            </Table>

            <Menu
                id="long-menu"
                anchorEl={this.state.anchorEl}
                open={this.state.menuVisible}
                onClose={this.closeRowMenu}
                onExited={this.clearMenuAnchor}
            >
                {this.getFileActions()}
            </Menu>
        </React.Fragment>;
    }

    render() {
        const {classes} = this.props;

        return <div className="contentPadding-full">
            <Grid container spacing={3}>
                <Grid item xs={4}>
                    <Paper className="root" elevation={0}>
                        <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                            <div className={classes.title}>
                                <Typography variant="h6" style={{textAlign: "center"}}>
                                    Anomalies
                                </Typography>

                                <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                    {!this.state.loading ? this.state.totalRecords : <Skeleton width={150}/>}
                                </Typography>
                            </div>
                        </div>
                    </Paper>
                </Grid>

                <Grid item xs={4}>
                    <Paper className="root" elevation={0}>
                        <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                            <div className={classes.title}>
                                <Typography variant="h6" style={{textAlign: "center"}}>
                                    Battles
                                </Typography>

                                <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                    {!this.state.loading ?  this.state.battles : <Skeleton width={150}/>}
                                </Typography>
                            </div>
                        </div>
                    </Paper>
                </Grid>

                <Grid item xs={4}>
                    <Paper className="root" elevation={0}>
                        <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                            <div className={classes.title}>
                                <Typography variant="h6" style={{textAlign: "center"}}>
                                    Ratio
                                </Typography>

                                <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                    {!this.state.loading ?
                                        `${parseFloat((this.state.totalRecords || 0) / (this.state.battles || 1) * 100).toFixed(2)} %` :
                                        <Skeleton width={150}/>}
                                </Typography>
                            </div>
                        </div>
                    </Paper>
                </Grid>
            </Grid>

            <Paper className="root" elevation={0}>
                <CardHeader
                    title="Anomalies"
                    action={
                        <React.Fragment>
                            <TextField
                                placeholder="Search…"
                                onKeyUp={this.searchInput}
                                onKeyDown={(e) => e.key === "Enter" ? this.searchForm() : null}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                variant="outlined"
                                size="small"
                            />
                            <div className={classes.inlineBlock}>
                                <Button disableElevation variant="contained" onClick={this.searchForm}>Search</Button>
                            </div>
                        </React.Fragment>
                    }
                />

                <div style={{float: "left", width: "100%"}}>
                    {this.pageContent(this.state.anomalies)}
                </div>
            </Paper>
        </div>;
    }
}

const styles: any = (theme: any) => ({
    inlineBlock: {
        display: "inline-block",
        marginLeft: 24,
    },
});

export const Simulation = withStyles(styles)(SimulationWrapped);
